import styled from 'styled-components';

import { Spinner } from '../../../../../../components/atoms/spinner/spinner';
import { Drawer } from '../../../../../../components/molecules/drawer/drawer';
import { ErrorState } from '../../../../../../components/molecules/error-state/error-state';
import {
  ClienteDetail,
} from '../../../../../../types/cliente';
import {
  ClienteProdutoDetail,
} from '../../../../../../types/cliente-produto';
import { ClienteKycBadge } from '../../../../components/cliente-kyc-badge';
import { formatPreco, simNao } from './format';
import { ProdutoStatusBadge } from './produto-status-badge';
import { ProdutoTipoBadge } from './produto-tipo-badge';
import { AfiliadosSection } from './sections/afiliados-section';
import { ComercialSection } from './sections/comercial-section';
import { ConteudoSection } from './sections/conteudo-section';
import { IdentificacaoSection } from './sections/identificacao-section';
import { IntegracoesSection } from './sections/integracoes-section';
import { PayloadTecnicoSection } from './sections/payload-tecnico-section';
import { RelacoesSection } from './sections/relacoes-section';

interface Props {
  isOpen: boolean;
  cliente: ClienteDetail | null;
  detail: ClienteProdutoDetail | null;
  status: 'idle' | 'loading' | 'error';
  errorMessage?: string | null;
  onClose: () => void;
}

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 80px 0;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const HeaderInfo = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 16px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Subtitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.secondary};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ProdutoDetailDrawer = ({
  isOpen,
  cliente,
  detail,
  status,
  errorMessage,
  onClose,
}: Props) => (
  <Drawer
    isOpen={isOpen}
    onClose={onClose}
    width={720}
    title={detail?.identificacao.nome ?? 'Detalhes do produto'}
    description={
      detail
        ? formatPreco(detail.comercial.preco, detail.comercial.moedaSigla)
        : undefined
    }
  >
    {status === 'loading' && (
      <Loading>
        <Spinner /> Carregando detalhes do produto…
      </Loading>
    )}

    {status === 'error' && (
      <ErrorState
        title="Não foi possível carregar o produto"
        description={errorMessage ?? 'Tente novamente mais tarde.'}
      />
    )}

    {detail && status !== 'loading' && (
      <>
        <HeaderInfo>
          <TopRow>
            <div>
              <Title>{detail.identificacao.nome || 'Produto sem nome'}</Title>
              <Subtitle>
                {detail.produtor.nome} {detail.produtor.sobrenome ?? ''} ·{' '}
                {detail.produtor.email}
              </Subtitle>
            </div>
            <BadgeRow>
              <ProdutoStatusBadge ativo={detail.ativo} />
              <ProdutoTipoBadge
                tipo={(detail.comercial.tipo as 1 | 2 | 3 | 4 | null) ?? null}
                descricao={detail.comercial.tipoDescricao}
              />
              {cliente && (
                <ClienteKycBadge
                  status={cliente.statusKyc}
                  description={cliente.statusKycDescricao}
                />
              )}
            </BadgeRow>
          </TopRow>
          <Subtitle>
            Preço: {formatPreco(detail.comercial.preco, detail.comercial.moedaSigla)}{' '}
            · Recorrente: {simNao(detail.comercial.recorrencia)}
          </Subtitle>
        </HeaderInfo>

        <Sections>
          <IdentificacaoSection data={detail.identificacao} />
          <ComercialSection data={detail.comercial} />
          <ConteudoSection data={detail.conteudo} />
          <AfiliadosSection data={detail.afiliados} />
          <IntegracoesSection data={detail.integracoes} />
          <RelacoesSection data={detail} />
          <PayloadTecnicoSection payload={detail.payloadTecnico} />
        </Sections>
      </>
    )}
  </Drawer>
);
